import {
  Form,
  Col,
  Button,
  Space,
  Input,
  Select,
  InputNumber,
  Radio,
  DatePicker,
} from 'antd';

import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';

import { WrapperSelect, BrickCodeInput } from 'common/components';

import { useGetProductEnums } from 'hooks';

import { NUMERIC_TYPE } from 'static/Constants';

import { findProductPropertyEnums } from 'pages/product-full-view/components/product-detail-view/utils';
import sortByKey from 'utils/sortByKey';
import { sortTargetMarket } from 'common/components/product-add/components/utils/sortTargetMarket';

import { useRequiredActiveProductContext } from './hooks';
import { useGetTargetMarketList } from 'hooks/selfLoadedProductHooks';
import { RequiredProductImagery } from 'common/components/product-add/make-product-active/RequiredProductImagery';
import {
  LENGTH_PRODUCT_DESCRIPTION,
  LENGTH_PRODUCT_NAME,
  MAX_NUMBER_INPUT_UPC,
} from 'common/components/product-add/components/constants';
import {
  getRestrictedTextValidationRules,
  getStandardTextValidationRules,
  validateGTIN,
  validateUPC12,
  validateUPC13,
  validateUPCE,
} from 'common/components/product-add/components/utils/validation';

import { RequiredBrand } from 'common/components/product-add/make-product-active/RequiredBrand';
import { lowercaseFirstLetter } from 'pages/product-full-view/shared/utils';

export const RequiredPropertyInput = ({
  property,
  status,
  entityId,
  primaryErrors,
  productImagery,
  mode,
  validationTimestamp,
}) => {
  const { productEnums } = useGetProductEnums();
  const targetMarketList = useGetTargetMarketList();

  const { requiredForm } = useRequiredActiveProductContext();

  const {
    dataType,
    listName,
    isMultipleValue,
    fieldFullPath,
    fullPathDisplayName,
  } = property;
  const lowercaseDataType = dataType.toLowerCase();
  const lowercaseNumberType = NUMERIC_TYPE.map((type) => type.toLowerCase());
  const lowercaseFieldFullPath = fieldFullPath?.toLowerCase();

  const isBrickCode = lowercaseFieldFullPath === 'brickcode';
  const isBrickCodeName = lowercaseFieldFullPath === 'brickcodename';

  const isGtin = lowercaseFieldFullPath === 'gtin';
  const isUpc8 = lowercaseFieldFullPath === 'upc8';
  const isUpc12 = lowercaseFieldFullPath === 'upc12';
  const isUpc13 = lowercaseFieldFullPath === 'upc13';
  const isProductDescription = lowercaseFieldFullPath === 'productdescription';
  const isBrand = lowercaseFieldFullPath === 'brandid';

  const errorMessage =
    primaryErrors.find(
      (error) => error?.fieldName?.toLowerCase() === fieldFullPath
    )?.errorMessage ?? `${fullPathDisplayName} is required.`;

  if (isBrickCode) {
    return (
      <RequiredBrickCode
        fieldFullPath={fieldFullPath}
        requiredForm={requiredForm}
        errorMessage={errorMessage}
      />
    );
  }

  if (isBrickCodeName) {
    return (
      <RequiredLayoutInput>
        <RequiredText
          fieldFullPath={fieldFullPath}
          status={status}
          disabled
          errorMessage={errorMessage}
        />
      </RequiredLayoutInput>
    );
  }

  if (isProductDescription) {
    const rules = [
      {
        required: true,
        message: errorMessage,
      },
      ...getStandardTextValidationRules(),
    ];

    return (
      <RequiredLayoutInput>
        <RequiredFormItem
          fieldFullPath={fieldFullPath}
          noStyle={false}
          rules={rules}
        >
          <Input.TextArea
            rows={6}
            status={status}
            maxLength={LENGTH_PRODUCT_DESCRIPTION}
            showCount
          />
        </RequiredFormItem>
      </RequiredLayoutInput>
    );
  }

  if (isGtin) {
    return (
      <RequiredLayoutInput>
        <RenderGtinInput
          fieldFullPath={fieldFullPath}
          status={status}
          errorMessage={errorMessage}
        />
      </RequiredLayoutInput>
    );
  }

  if (isUpc8) {
    return (
      <RequiredLayoutInput>
        <RenderUpc8Input
          fieldFullPath={fieldFullPath}
          status={status}
          errorMessage={errorMessage}
        />
      </RequiredLayoutInput>
    );
  }

  if (isUpc12) {
    return (
      <RequiredLayoutInput>
        <RenderUpc12Input
          fieldFullPath={fieldFullPath}
          status={status}
          errorMessage={errorMessage}
        />
      </RequiredLayoutInput>
    );
  }

  if (isUpc13) {
    return (
      <RequiredLayoutInput>
        <RenderUpc13Input
          fieldFullPath={fieldFullPath}
          status={status}
          errorMessage={errorMessage}
        />
      </RequiredLayoutInput>
    );
  }

  if (isBrand) {
    return <RequiredBrand formInstance={requiredForm} />;
  }

  if (fieldFullPath === 'TargetMarket') {
    const listCountry = sortTargetMarket(targetMarketList);

    return (
      <RequiredTargetMarket
        isMultipleValue={isMultipleValue}
        listCountry={listCountry}
        fieldFullPath={fieldFullPath}
        status={status}
        errorMessage={errorMessage}
      />
    );
  }

  if (listName) {
    let propertyEnums = findProductPropertyEnums(listName, productEnums);

    if (
      fieldFullPath === 'NetContentsUom' ||
      fieldFullPath === 'PrimarySizeUOM'
    ) {
      propertyEnums =
        propertyEnums.filter((type) =>
          ['Mass', 'Count', 'Volume', 'Dimensions', 'Area'].includes(
            type.enumGroup
          )
        ) || [];
    }

    if (fieldFullPath === 'DimensionUOM') {
      propertyEnums =
        propertyEnums.filter((type) => type.enumGroup === 'Dimensions') || [];
    }

    if (fieldFullPath === 'WeightUOM') {
      propertyEnums =
        propertyEnums.filter((type) => type.enumGroup === 'Mass') || [];
    }

    return (
      <RequiredLayoutInput>
        <RequiredSelect
          isMultipleValue={isMultipleValue}
          propertyEnums={propertyEnums}
          fieldFullPath={fieldFullPath}
          status={status}
          errorMessage={errorMessage}
        />
      </RequiredLayoutInput>
    );
  }

  if (isMultipleValue) {
    return (
      <RequiredLayoutInput>
        <RequiredList
          dataType={dataType}
          fieldFullPath={fieldFullPath}
          errorMessage={errorMessage}
        />
      </RequiredLayoutInput>
    );
  }

  if (lowercaseDataType === 'image') {
    return (
      <RequiredLayoutInput>
        <RequiredProductImagery
          entityId={entityId}
          fieldFullPath={fieldFullPath}
          displayName={property.displayName}
          primaryErrors={primaryErrors}
          productImagery={productImagery}
          mode={mode}
          validationTimestamp={validationTimestamp}
        />
      </RequiredLayoutInput>
    );
  }

  if (lowercaseDataType === 'string') {
    const isProductName = fieldFullPath?.toLowerCase() === 'productname';
    const rules = isProductName
      ? [
          {
            required: true,
            message: errorMessage,
          },
          ...getRestrictedTextValidationRules('Product Name'),
        ]
      : [
          {
            required: true,
            message: errorMessage,
          },
        ];

    return (
      <RequiredLayoutInput>
        <RequiredText
          fieldFullPath={fieldFullPath}
          status={status}
          errorMessage={errorMessage}
          rules={rules}
          maxLength={isProductName ? LENGTH_PRODUCT_NAME : undefined}
        />
      </RequiredLayoutInput>
    );
  }

  if (lowercaseNumberType.includes(lowercaseDataType)) {
    return (
      <RequiredLayoutInput>
        <RequiredNumber
          fieldFullPath={fieldFullPath}
          status={status}
          errorMessage={errorMessage}
        />
      </RequiredLayoutInput>
    );
  }

  if (lowercaseDataType === 'boolean') {
    return (
      <RequiredLayoutInput>
        <RequiredBoolean
          fieldFullPath={fieldFullPath}
          errorMessage={errorMessage}
        />
      </RequiredLayoutInput>
    );
  }

  if (lowercaseDataType === 'datetime') {
    return (
      <RequiredLayoutInput>
        <RequiredDatetime
          fieldFullPath={fieldFullPath}
          errorMessage={errorMessage}
        />
      </RequiredLayoutInput>
    );
  }

  return <RequiredLayoutInput>Under constructor</RequiredLayoutInput>;
};

const RequiredBrickCode = ({ fieldFullPath, requiredForm, errorMessage }) => {
  return (
    <>
      <RequiredFormItem
        fieldFullPath={fieldFullPath}
        hidden
        errorMessage={errorMessage}
      >
        render hidden input to collect brick code
      </RequiredFormItem>
      <BrickCodeInput
        updateBrickCodeData={(brickCodeValues) => {
          const currentFormValues = requiredForm.getFieldsValue();

          requiredForm.setFieldsValue({
            ...currentFormValues,
            ...brickCodeValues,
          });
        }}
      />
    </>
  );
};

const RequiredText = ({
  fieldFullPath,
  disabled,
  status,
  errorMessage,
  rules: customRules,
  maxLength,
  ...otherProps
}) => {
  return (
    <RequiredFormItem
      fieldFullPath={fieldFullPath}
      errorMessage={errorMessage}
      rules={customRules}
      {...otherProps}
    >
      <Input
        status={status}
        disabled={disabled}
        maxLength={maxLength}
        showCount={!!maxLength}
      />
    </RequiredFormItem>
  );
};

const RequiredNumber = ({
  fieldFullPath,
  status,
  errorMessage,
  ...otherProps
}) => {
  return (
    <RequiredFormItem
      fieldFullPath={fieldFullPath}
      errorMessage={errorMessage}
      {...otherProps}
    >
      <InputNumber status={status} style={{ width: '100%' }} />
    </RequiredFormItem>
  );
};

const RequiredBoolean = ({ fieldFullPath, errorMessage, ...otherProps }) => {
  return (
    <RequiredFormItem
      fieldFullPath={fieldFullPath}
      errorMessage={errorMessage}
      {...otherProps}
    >
      <Radio.Group>
        <Radio value={true}>True</Radio>
        <Radio value={false}>False</Radio>
      </Radio.Group>
    </RequiredFormItem>
  );
};

const RequiredDatetime = ({ fieldFullPath, errorMessage, ...otherProps }) => {
  return (
    <RequiredFormItem
      fieldFullPath={fieldFullPath}
      errorMessage={errorMessage}
      {...otherProps}
    >
      <DatePicker style={{ width: '60%' }} />
    </RequiredFormItem>
  );
};

const RequiredSelect = (props) => {
  const {
    propertyEnums,
    fieldFullPath,
    isMultipleValue,
    status,
    errorMessage,
    ...otherProps
  } = props;

  return (
    <RequiredFormItem
      fieldFullPath={fieldFullPath}
      errorMessage={errorMessage}
      {...otherProps}
    >
      <WrapperSelect
        showSearch
        mode={isMultipleValue ? 'multiple' : ''}
        filterOption={(input, option) =>
          option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
        }
        style={{ width: '100%' }}
        status={status}
      >
        {sortByKey(propertyEnums, 'enumDisplayName')?.map((property, index) => (
          <Select.Option
            value={property.enumDisplayName}
            key={property.enumDisplayName + index}
          >
            {property.enumDisplayName
              ? property.enumDisplayName
              : property.enumDescription}
          </Select.Option>
        ))}
      </WrapperSelect>
    </RequiredFormItem>
  );
};

const RequiredTargetMarket = (props) => {
  const {
    listCountry,
    fieldFullPath,
    isMultipleValue,
    status,
    errorMessage,
    ...otherProps
  } = props;

  return (
    <RequiredFormItem
      fieldFullPath={fieldFullPath}
      errorMessage={errorMessage}
      className='required-section__target-market'
      {...otherProps}
    >
      <WrapperSelect
        showSearch
        mode={isMultipleValue ? 'multiple' : ''}
        filterOption={(input, option) =>
          option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
        }
        style={{ width: '100%' }}
        status={status}
      >
        {listCountry?.map((item, index) => (
          <Select.Option key={index} value={item?.Country}>
            {item?.Country}
          </Select.Option>
        ))}
      </WrapperSelect>
    </RequiredFormItem>
  );
};

const RequiredLayoutInput = ({ children, ...otherProps }) => {
  return (
    <Col flex={1} {...otherProps}>
      {children}
    </Col>
  );
};

const RequiredFormItem = ({
  fieldFullPath,
  children,
  errorMessage,
  rules: customRules,
  ...otherProps
}) => {
  const mergedRules = customRules || [
    {
      required: true,
      message: errorMessage,
    },
  ];

  return (
    <Form.Item
      label={null}
      name={lowercaseFirstLetter(fieldFullPath)}
      validateTrigger={['onChange', 'onBlur']}
      rules={mergedRules}
      {...otherProps}
    >
      {children}
    </Form.Item>
  );
};

const RequiredList = ({ fieldFullPath, dataType, errorMessage }) => {
  const isDatetimeType = dataType === 'datetime';
  const isNumberType = NUMERIC_TYPE.includes(dataType);

  return (
    <Form.List
      name={fieldFullPath}
      rules={[
        {
          validator: async (_, items) => {
            if (!items || items.length === 0) {
              return Promise.reject(new Error(errorMessage));
            }
            return Promise.resolve();
          },
        },
      ]}
    >
      {(fields, { add, remove }, { errors }) => {
        return (
          <>
            <div>
              {fields.map((field) => (
                <div
                  key={field.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Space>
                    <Form.Item {...field} noStyle>
                      {isDatetimeType ? (
                        <DatePicker />
                      ) : isNumberType ? (
                        <InputNumber />
                      ) : (
                        <Input style={{ width: '100%' }} />
                      )}
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  </Space>
                </div>
              ))}
            </div>
            <div>
              <Button
                type='dashed'
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Add value
              </Button>
            </div>
            <Form.ErrorList errors={errors} />
          </>
        );
      }}
    </Form.List>
  );
};

const RenderGtinInput = ({
  fieldFullPath,
  status,
  errorMessage,
  ...otherProps
}) => {
  return (
    <RequiredFormItem
      fieldFullPath={fieldFullPath}
      validateTrigger={['onBlur', 'onChange']}
      rules={[
        {
          required: true,
          message: errorMessage,
        },
        {
          validator: (_, value) => {
            if (!value) {
              return Promise.resolve();
            }
            const validation = validateGTIN(value);
            if (!validation.isValid) {
              return Promise.reject(new Error(validation.error));
            }
            return Promise.resolve();
          },
        },
      ]}
      noStyle={false}
      {...otherProps}
    >
      <Input
        min='0'
        maxLength={MAX_NUMBER_INPUT_UPC}
        status={status}
        style={{ marginTop: 8 }}
      />
    </RequiredFormItem>
  );
};

const RenderUpc12Input = ({ fieldFullPath, errorMessage, ...otherProps }) => {
  return (
    <RequiredFormItem
      fieldFullPath={fieldFullPath}
      style={{ marginRight: 12 }}
      name='upc12'
      hasFeedback
      validateTrigger={['onBlur', 'onChange']}
      rules={[
        {
          required: true,
          message: errorMessage,
        },
        {
          validator: (_, value) => {
            if (!value) {
              return Promise.resolve();
            }
            const validation = validateUPC12(value);
            if (!validation.isValid) {
              return Promise.reject(new Error(validation.error));
            }
            return Promise.resolve();
          },
        },
      ]}
      {...otherProps}
    >
      <Input min='0' maxLength={MAX_NUMBER_INPUT_UPC} />
    </RequiredFormItem>
  );
};

const RenderUpc13Input = ({ fieldFullPath, errorMessage, ...otherProps }) => {
  return (
    <RequiredFormItem
      fieldFullPath={fieldFullPath}
      style={{ marginRight: 12 }}
      name='upc13'
      hasFeedback
      validateTrigger={['onBlur', 'onChange']}
      rules={[
        {
          required: true,
          message: errorMessage,
        },
        {
          validator: (_, value) => {
            if (!value) {
              return Promise.resolve();
            }
            const validation = validateUPC13(value);
            if (!validation.isValid) {
              return Promise.reject(new Error(validation.error));
            }
            return Promise.resolve();
          },
        },
      ]}
      {...otherProps}
    >
      <Input min='0' maxLength={MAX_NUMBER_INPUT_UPC} />
    </RequiredFormItem>
  );
};

const RenderUpc8Input = ({ fieldFullPath, errorMessage, ...otherProps }) => {
  return (
    <RequiredFormItem
      fieldFullPath={fieldFullPath}
      style={{ marginRight: 12 }}
      name='upc8'
      hasFeedback
      validateTrigger={['onBlur', 'onChange']}
      rules={[
        {
          required: true,
          message: errorMessage,
        },
        {
          validator: (_, value) => {
            if (!value) {
              return Promise.resolve();
            }
            const validation = validateUPCE(value);
            if (!validation.isValid) {
              return Promise.reject(new Error(validation.error));
            }
            return Promise.resolve();
          },
        },
      ]}
      {...otherProps}
    >
      <Input min='0' maxLength={MAX_NUMBER_INPUT_UPC} />
    </RequiredFormItem>
  );
};
